// src/app/core/services/dialog.service.ts
import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { AppExceptionModel } from '../../../models/app-exception.model';
import { AlertType } from './confirm.dialog.model';
import { DlgConfirmComponent } from './dlg-confirm/dlg-confirm.component';
import {
  DlgPlanLimitComponent,
  PlanLimitDialogData,
} from './dlg-plan-limit/dlg-plan-limit.component';
import {
  DlgPaymentPendingComponent,
  PaymentPendingDialogData,
} from './dlg-payment-pending/dlg-payment-pending.component';

export interface ConfirmDialogData {
  title: string;
  description?: string;
  action?: string;
  cancelText?: string;
  buttonColor?: string;
  buttonTextColor?: string;
  showType?: AlertType;
  alertMode?: boolean; // true = only "Okay", false = "Cancel + Action"
}

@Injectable({
  providedIn: 'root',
})
export class DialogService {
  constructor(private matDialog: MatDialog) {}

  // ──────────────────────────────────────────────────────────────
  // 1. Show simple alert (one button)
  // ──────────────────────────────────────────────────────────────
  alert(data: Omit<ConfirmDialogData, 'alertMode'>): Promise<boolean> {
    return this.open({ ...data, alertMode: true });
  }

  // ──────────────────────────────────────────────────────────────
  // 2. Show confirmation dialog (two buttons)
  // ──────────────────────────────────────────────────────────────
  confirm(data: Omit<ConfirmDialogData, 'alertMode'>): Promise<boolean> {
    return this.open({ ...data, alertMode: false });
  }

  // ──────────────────────────────────────────────────────────────
  // 3. Convenience: Show error from AppException
  // ──────────────────────────────────────────────────────────────
  showError(error: AppExceptionModel): void {
    this.alert({
      title: error.title ?? 'Error',
      description: error.message,
      showType: AlertType.error,
      action: 'Okay',
      buttonColor: 'bg-primary-500',
      buttonTextColor: 'text-white',
    });
  }

  // ──────────────────────────────────────────────────────────────
  // 4. Internal: Open dialog and return promise
  // ──────────────────────────────────────────────────────────────
  private open(data: ConfirmDialogData): Promise<boolean> {
    const config = new MatDialogConfig<any>();
    config.data = {
      title: data.title,
      description: data.description ?? 'Are you sure?',
      action: data.action ?? (data.alertMode ? 'Okay' : 'Yes'),
      cancelText: data.cancelText ?? 'Cancel',
      buttonColor: data.buttonColor ?? 'bg-primary-500',
      buttonTextColor: data.buttonTextColor ?? 'text-white',
      showType: data.showType ?? AlertType.warning,
      alertMode: data.alertMode ?? false,
    };
    config.width = '420px';
    config.maxWidth = '90vw';
    config.autoFocus = false;
    config.panelClass = 'custom-dialog-no-padding'; // optional: remove MatDialog padding

    const dialogRef = this.matDialog.open(DlgConfirmComponent, config);

    return dialogRef
      .afterClosed()
      .toPromise()
      .then((result) => !!result);
  }

  // ──────────────────────────────────────────────────────────────
  // 5. Show plan limit dialog
  // ──────────────────────────────────────────────────────────────
  openPlanLimit(data: PlanLimitDialogData): Promise<boolean> {
    return DlgPlanLimitComponent.open(this.matDialog, data)
      .afterClosed()
      .toPromise()
      .then((result) => !!result);
  }

  // ──────────────────────────────────────────────────────────────
  // 6. Show payment pending / feature lockout dialog
  // ──────────────────────────────────────────────────────────────
  openPaymentPending(data: PaymentPendingDialogData): Promise<boolean> {
    return DlgPaymentPendingComponent.open(this.matDialog, data)
      .afterClosed()
      .toPromise()
      .then((result) => !!result);
  }

  // Close all open dialogs
  closeAll(): void {
    this.matDialog.closeAll();
  }
}
